import { Reducer } from 'redux';
import { IDocumentState, DocActionTypes } from './types';
import { StudentActionTypes } from '../types';
import { DataActionTypes } from '../../data/types';
import { UIActionTypes } from '../../ui/types';
import { IAnyObj, IDocument } from '../../../types';
import getBirthCertRequirements from '../../../components/pages/modals/birthCertificate/getHighlightedErrorSections';
import { transformObject } from './helpers';
import { getDocDetails } from '../../../utilities/documents';

const initialState: IDocumentState = {
  cas: {},
  passport: {},
  academicTranscript: {},
  academicCertificate: {},
  studentVisas: {},
  visaRefusals: {},
  nonUkVisaRefusals: {},
  otherVisas: {},
  parentalConsent: {},
  birthCertificate: {},
  tbTest: {},
  atas: {},
  englishTest: {},
  englishQualification: {},
  customRequest: {},
  postStudyLetter: {}
};

const mainReducer: Reducer<IDocumentState> = (state = initialState, action) => {
  switch (action.type) {
    case StudentActionTypes.GET_STUDENT_DOCS_SUCCESS:
      const docsTransformed = transformObject(action.payload.documents);
      return {
        ...state,
        ...docsTransformed
      };
    case StudentActionTypes.SAVE_PERSONAL_INFO_SUCCESS:
    case StudentActionTypes.SAVE_HISTORY_SUCCESS:
    case StudentActionTypes.SAVE_CONSENTS_SUCCESS:
    case StudentActionTypes.SAVE_PRE_CAS_INTERVIEW_SUCCESS:
    case StudentActionTypes.SAVE_VISA_PREP_FORM_SUCCESS:
    case StudentActionTypes.SAVE_DOCUMENTS_SUCCESS:
    case StudentActionTypes.SAVE_FINANCE_DOCUMENTS_SUCCESS:
    case DocActionTypes.UPDATE_ATAS_SUCCESS:
    case DocActionTypes.UPDATE_VISA_REFUSAL_META_DATA_SUCCESS:
    case DocActionTypes.UPDATE_TB_SUCCESS:
    case DocActionTypes.ADD_REQUIRED_DOCUMENT_SUCCESS:
    case DocActionTypes.TOGGLE_ACKNOWLEDGE_DOC_SUCCESS: {
      let updatedState = updateAcknowledgeState(state, action.payload);
      return updatedState;
    }

    case UIActionTypes.OPEN_UPLOAD_MODAL: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, metadata } = action.payload;

      if (!metadata) {
        return state;
      }
      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

      // extend the object with new metadata, no need to craete new object ref
      Object.entries(metadata).forEach(([key, value]) => {
        if (key === 'isDocumentFixed') {
          // don't update state as fixed until file(s) have been uploaded
          newRecord.isDocumentFixedPending = value;
          newRecord.docCount = fileNames.length;
          if (newRecord.isDocumentFixed) {
            delete newRecord.isDocumentFixed;
          }
        } else if (value !== undefined && key !== 'shouldShowRequirements') {
          newRecord[key] = value;
        }
      });

      return {
        ...newState
      };
    }
    case DataActionTypes.UPLOAD_FILE_SUCCESS: {
      const { sponsorType, sponsorKey, docType, docKey, fileNames, isFinanceDocumentModal, isFollowUpModal } =
        action.payload;

      if (isFinanceDocumentModal || isFollowUpModal) {
        return state;
      }

      const newState = { ...state };
      const newRecord = getDocDetails({ docs: newState, sponsorType, sponsorKey, docType, docKey }) as IAnyObj;

      // strip out file upload failures
      let parsedFileNames: string[] = fileNames;
      parsedFileNames = fileNames?.filter((fName: string) => !fName.includes('upload--error--')) ?? [];
      newRecord.fileNames = parsedFileNames;

      let hasRequirements = false;
      switch (sponsorType) {
        case 'birthCertificate': {
          switch (docType) {
            case 'certificate':
              hasRequirements = getBirthCertRequirements(newRecord).length > 0;
              break;
          }
          break;
        }
        case 'parentalConsent': {
          hasRequirements = !newRecord.isSigned || !newRecord.isDated;
          break;
        }
      }

      // if birth certificate has requested a translation and hasRequirement
      // only update the status to 'UPLOADED_WITH_ERROR' if the student is required
      // to re-upload another certificate
      if (
        sponsorType === 'birthCertificate' &&
        docType === 'certificate' &&
        newRecord.status === 'REPLACED' &&
        newRecord.isLegalDocument === true
      ) {
        return {
          ...newState
        };
      } else {
        newRecord.status = hasRequirements ? 'UPLOADED_WITH_ERROR' : 'UPLOADED_OK';
      }

      return {
        ...newState
      };
    }

    default: {
      return state;
    }
  }
};

export { mainReducer as documentsReducer };

function setNestedStateValue(state, keyPath, newValue) {
  const keys = keyPath.split('#');

  let current = state;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }

  const finalKey = keys[keys.length - 1];
  current[finalKey] = {
    ...current[finalKey],
    ...newValue
  };
  return current[finalKey];
}

function updateAcknowledgeState(state, payload) {
  const { documents } = payload;
  for (const key in documents) {
    if (documents.hasOwnProperty(key)) {
      const newValue = documents[key];

      setNestedStateValue(state, key, newValue);
    }
  }

  return {
    ...state,
    documents: {
      ...state.documents
    }
  };
}
